import {
	ContentBlogUnifiedQueryArtifact,
	ContentUnifiedQueryArtifact,
	ContentUnifiedWithSpaceKeyQueryArtifact,
	type ContentBlogUnifiedQueryType,
	type ContentBlogUnifiedQueryVariablesType,
	type ContentUnifiedQueryType,
	type ContentUnifiedQueryVariablesType,
	type ContentUnifiedWithSpaceKeyQueryType,
	type ContentUnifiedWithSpaceKeyQueryVariablesType,
} from '@confluence/content-unified-query';
import { fg } from '@confluence/feature-gating';
import { isUnauthorizedError } from '@confluence/query-preloader-tools/entry-points/isUnauthorizedError.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { ssrPermissionsErrorHandler } from '@confluence/ssr-utilities/entry-points/SSRPermissionsErrorHandler.ts';

type PreloadContentParameters = {
	contentId: string;
	isBlog: boolean;
	spaceKey: string;
	shouldPreloadUsingSpaceKey?: boolean;
	versionOverride?: number | null;
	userId?: string | null;
	agentSessionsEnabled?: boolean;
};

type PreloadContentResult = {
	result?: any;
	hasErrors: boolean;
};

type ChooseContentUnifiedQueryVariablesType =
	| ContentUnifiedQueryVariablesType
	| ContentBlogUnifiedQueryVariablesType;

const DEFAULT_BYLINE_CONTRIBUTORS_FETCH_LIMIT = 4;

function processContentPreloadPermissionError(result: any) {
	result?.errors?.forEach((error: any) => {
		// The "Cannot find space" condition is handled by redirecting to the correct space
		// even if error is not propagated to ContentPrerequisites where it is normally handled.
		if (isUnauthorizedError(error)) {
			ssrPermissionsErrorHandler.markSSRPreloadPermissionError('view', error);
		}
	});
}

export const transformContentData = (
	data: any,
): {
	content: {
		nodes: any[];
		// @ts-ignore
		__typename: string;
	} | null;
	space: any;
} => ({
	content: data?.spaceHomepage
		? {
				nodes: [data?.spaceHomepage],
				// @ts-ignore
				__typename: 'PaginatedContentListWithChild',
			}
		: null,
	space: data?.space || null,
});

export function preloadContent({
	contentId,
	spaceKey,
	isBlog,
	versionOverride = null,
	userId,
	agentSessionsEnabled = false,
}: PreloadContentParameters): Promise<PreloadContentResult> {
	window.performance.mark('CFP-63.preloadContent.start');
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = Boolean(process.env.REACT_SSR);

	// Space overview
	if (spaceKey && !contentId) {
		return query<ContentUnifiedWithSpaceKeyQueryType, ContentUnifiedWithSpaceKeyQueryVariablesType>(
			{
				query: ContentUnifiedWithSpaceKeyQueryArtifact,
				variables: {
					spaceKey,
					versionOverride,
					isSSR,
					useNewContentTopper: true,
					// Fetch the agent-session marker on the homepage node so the data bridged into the
					// ContentUnifiedQuery cache entry below is complete and matches the live read's
					// selection set (avoids a partial-data cache miss -> Tiny renderer).
					...(agentSessionsEnabled && userId
						? {
								agentSessionsPropertyKey: `cc-agent-mention-sessions:${userId}`,
								fetchAgentSessionsMarker: true,
							}
						: {}),
					// Keep this in lockstep with the ContentUnifiedQuery write below AND the
					// ViewPageContainer read: the agent-trust-receipt fields must be fetched here so
					// the transformed data written into the ContentUnifiedQuery cache slot is complete.
					...(fg('cc_sparks_agent_trust_receipt') ? { includeAgentTrustReceiptData: true } : {}),
					...(fg('confluence_vp_meta_frag_reader') ? { preloadLabels: true } : {}),
					...(fg('confluence_vp_meta_frag_reader') ? { includeClassification: true } : {}),
				},
				errorPolicy: 'all',
			},
		).then((result: any) => {
			// Record the end mark and timestamp to track *both* duration of preloading
			// and timestamp of the moment when preloading finished
			window.performance.mark('CFP-63.preloadContent.end');
			window.performance.mark('CFP-63.preloadContentEnd');

			const data = result?.data;
			const homepageId = data?.spaceHomepage?.id;
			if (homepageId) {
				// update result with transformed data
				result.data = transformContentData(data);

				writeQueryInterop<ContentUnifiedQueryType, ContentUnifiedQueryVariablesType>({
					query: ContentUnifiedQueryArtifact,
					variables: {
						contentId: homepageId,
						spaceKey,
						versionOverride,
						isSSR,
						useNewContentTopper: true,
						// Match the variables that ViewPageContainer sends when the experiment is enabled,
						// so the bridged cache entry shares the same key as the live read and is a hit.
						// Without this, the space-overview homepage reads ContentUnifiedQuery with the
						// agent-session vars while this entry was written without them → cache miss →
						// body is null on first paint → page renders with the Tiny renderer.
						...(agentSessionsEnabled && userId
							? {
									agentSessionsPropertyKey: `cc-agent-mention-sessions:${userId}`,
									fetchAgentSessionsMarker: true,
								}
							: {}),
						// Must match the variable set ViewPageContainer reads ContentUnifiedQuery
						// with, otherwise the cache key diverges and the homepage re-fetches on the
						// server.
						...(fg('cc_sparks_agent_trust_receipt') ? { includeAgentTrustReceiptData: true } : {}),
						...(fg('confluence_vp_meta_frag_reader') ? { preloadLabels: true } : {}),
						...(fg('confluence_vp_meta_frag_reader') ? { includeClassification: true } : {}),
					},
					data: { ...result.data },
				});
			}

			processContentPreloadPermissionError(result);

			return { result, hasErrors: result?.hasErrors };
		});
	}

	const chooseContentUnifiedQuery = isBlog
		? ContentBlogUnifiedQueryArtifact
		: ContentUnifiedQueryArtifact;
	const queryVariables: ChooseContentUnifiedQueryVariablesType = {
		contentId,
		spaceKey,
		versionOverride,
		isSSR,
		useNewContentTopper: true,
		// Match the variables that ViewPageContainer sends when the experiment is enabled,
		// so the preloaded cache entry is a hit and no second network request is made.
		...(agentSessionsEnabled && userId
			? {
					agentSessionsPropertyKey: `cc-agent-mention-sessions:${userId}`,
					fetchAgentSessionsMarker: true,
				}
			: {}),
		// Match the variable ViewPageContainer reads ContentUnifiedQuery with so the preloaded
		// cache entry is hit and the agent-trust-receipt fields are present on first client render.
		...(fg('cc_sparks_agent_trust_receipt') ? { includeAgentTrustReceiptData: true } : {}),
		...(fg('confluence_vp_title_byline_frag_dedup')
			? {
					includeBylineContributors: true,
					bylineContributorsLimit: DEFAULT_BYLINE_CONTRIBUTORS_FETCH_LIMIT,
				}
			: {}),
		...(fg('confluence_vp_meta_frag_reader') ? { preloadLabels: true } : {}),
		...(fg('confluence_vp_meta_frag_reader') ? { includeClassification: true } : {}),
	};

	if (isSSR) {
		(
			queryVariables as ContentUnifiedQueryVariablesType | ContentBlogUnifiedQueryVariablesType
		).preloadLabels = true;
	}

	return query<
		ContentUnifiedQueryType | ContentBlogUnifiedQueryType,
		ChooseContentUnifiedQueryVariablesType
	>({
		query: chooseContentUnifiedQuery,
		variables: queryVariables,
		errorPolicy: 'all',
	}).then((result: any) => {
		// Record the end mark and timestamp to track *both* duration of preloading
		// and timestamp of the moment when preloading finished
		window.performance.mark('CFP-63.preloadContent.end');
		window.performance.mark('CFP-63.preloadContentEnd');

		processContentPreloadPermissionError(result);

		return { result, hasErrors: result?.hasErrors };
	});
}
